/**
 * Share an activity (or any backend-rendered PDF) using the Web Share API.
 *
 * Strategy:
 *   1. Fetch the PDF bytes from the backend with auth.
 *   2. If `navigator.canShare` supports files → trigger the native OS share
 *      sheet (WhatsApp / Mail / etc.).
 *   3. Otherwise → fall back to a normal download.
 *
 * Returns one of: 'shared' | 'downloaded' | 'cancelled' | 'error'.
 *
 * Callers should always show a small toast after this resolves.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/**
 * @param {Object} params
 * @param {string} params.activityId       Required.
 * @param {string} [params.childId]        If set, backend overlays the child-specific customisation.
 * @param {string} params.activityName     Used for the share title + fallback filename.
 * @param {string} [params.childFirstName] Used to personalise the share text.
 */
export async function shareActivityPdf({ activityId, childId, activityName, childFirstName }) {
  if (!activityId) return 'error';
  const token = localStorage.getItem('token');
  if (!token) return 'error';

  const url = childId
    ? `${API}/activities/${activityId}/pdf?child_id=${encodeURIComponent(childId)}`
    : `${API}/activities/${activityId}/pdf`;

  let blob;
  try {
    const resp = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    blob = resp.data;
  } catch (e) {
    return 'error';
  }

  const safeName = (activityName || 'activity')
    .replace(/[^A-Za-z0-9_-]+/g, '_')
    .slice(0, 50) || 'activity';
  const filename = `PlayFirst_${safeName}.pdf`;
  const file = new File([blob], filename, { type: 'application/pdf' });

  const shareTitle = `${activityName || 'PlayFirst play idea'}`;
  const shareText = childFirstName
    ? `Here's a play idea for ${childFirstName} from PlayFirst.`
    : `Here's a play idea from PlayFirst.`;

  // Try native share sheet first (mobile + recent desktop Chrome/Edge).
  try {
    if (
      typeof navigator !== 'undefined'
      && navigator.canShare
      && navigator.canShare({ files: [file] })
      && navigator.share
    ) {
      try {
        await navigator.share({ files: [file], title: shareTitle, text: shareText });
        return 'shared';
      } catch (err) {
        // AbortError = user dismissed the share sheet; treat as cancelled.
        if (err && (err.name === 'AbortError' || /cancel|abort/i.test(err.message || ''))) {
          return 'cancelled';
        }
        // Other errors → fall through to download fallback.
      }
    }
  } catch {
    // Some browsers throw on canShare with files — ignore and fall through.
  }

  // Fallback: trigger a regular download.
  try {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return 'downloaded';
  } catch {
    return 'error';
  }
}
